import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "../components/Navbar";
import api from "../lib/api";
import { getIcon } from "../lib/icons";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { PRICING_ENABLED } from "../config";
import {
  Loader2, Award, Target, CheckCircle2, TrendingUp, ShieldCheck, ShieldOff,
  Lock, Medal, ScrollText, Sparkles, Plane, KeyRound,
} from "lucide-react";

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { type: 'ok'|'err', text }

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (next.length < 8) { setMsg({ type: "err", text: "New password must be at least 8 characters." }); return; }
    if (next !== confirm) { setMsg({ type: "err", text: "New password and confirmation do not match." }); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setMsg({ type: "ok", text: "Password updated. It will persist across restarts and deploys." });
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setMsg({ type: "err", text: typeof d === "string" ? d : "Could not update password. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 rounded-sm border border-border/70 bg-card" data-testid="change-password-card">
      <form onSubmit={submit} className="max-w-md space-y-3">
        <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
          placeholder="Current password" autoComplete="current-password" required
          className="rounded-sm" data-testid="current-password-input" />
        <Input type="password" value={next} onChange={(e) => setNext(e.target.value)}
          placeholder="New password (min 8 characters)" autoComplete="new-password" required
          className="rounded-sm" data-testid="new-password-input" />
        <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
          placeholder="Confirm new password" autoComplete="new-password" required
          className="rounded-sm" data-testid="confirm-password-input" />
        {msg && (
          <p className={`text-sm ${msg.type === "ok" ? "text-primary" : "text-destructive"}`} data-testid="change-password-message">
            {msg.text}
          </p>
        )}
        <Button type="submit" disabled={busy}
          className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-semibold"
          data-testid="change-password-submit">
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <KeyRound className="h-4 w-4 mr-1.5" />}
          Update Password
        </Button>
      </form>
    </div>
  );
}

export default function Profile() {
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);
  const [badges, setBadges] = useState([]);
  const [academies, setAcademies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/progress"), api.get("/badges"), api.get("/academies")])
      .then(([p, b, a]) => {
        setProgress(p.data);
        setBadges(b.data);
        setAcademies(a.data);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading || !progress)
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      </div>
    );

  const earned = new Set(progress.earned_badges || []);
  const awards = progress.badge_awards || {};
  const completed = progress.completed_lessons || [];
  const quizAvg = (() => {
    const vals = Object.values(progress.quiz_scores || {});
    if (!vals.length) return 0;
    return Math.round(vals.reduce((a, b) => a + b.percent, 0) / vals.length);
  })();
  const initials = (user?.name || "Pilot").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const stats = [
    { label: "Lessons Complete", value: `${progress.lessons_completed_count}/${progress.total_lessons}`, icon: CheckCircle2 },
    { label: "Overall Progress", value: `${progress.overall_percent}%`, icon: TrendingUp },
    { label: "Avg Quiz Score", value: `${quizAvg}%`, icon: Target },
    { label: "Badges Earned", value: `${earned.size}`, icon: Award },
  ];

  const academyProgress = (a) => {
    const ids = a.modules.flatMap((m) => m.lesson_ids || []);
    const done = ids.filter((id) => completed.includes(id)).length;
    return { pct: ids.length ? Math.round((done / ids.length) * 100) : 0, active: ids.length > 0 };
  };

  return (
    <div className="min-h-screen pb-16">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-8">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row items-center gap-6 p-7 rounded-sm border border-border/70 bg-card mb-8" data-testid="profile-header">
          <div className="h-20 w-20 rounded-sm bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0 overflow-hidden">
            {user?.picture ? <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
              : <span className="font-display font-black text-2xl text-primary">{initials}</span>}
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h1 className="font-display text-3xl font-bold">{user?.name}</h1>
            <p className="text-muted-foreground text-sm">{user?.email}</p>
            <div className="flex items-center justify-center sm:justify-start gap-3 mt-3 flex-wrap">
              <span className="text-[11px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-sm bg-secondary text-muted-foreground border border-border">
                {user?.role === "admin" ? "Commander" : "Cadet"}
              </span>
              {user?.has_paid ? (
                <span className="text-[11px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-sm bg-primary/10 text-primary border border-primary/40 flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5" /> Full Access
                </span>
              ) : (
                <span className="text-[11px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-sm bg-secondary/40 text-muted-foreground border border-border flex items-center gap-1.5">
                  <ShieldOff className="h-3.5 w-3.5" /> Free
                </span>
              )}
              {user?.founding_pilot && (
                <span title="Helped shape DronAcademy before its public launch."
                  data-testid="founding-pilot-badge"
                  className="text-[11px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-sm bg-accent/10 text-accent border border-accent/40 flex items-center gap-1.5">
                  <Plane className="h-3.5 w-3.5" /> Founding Pilot
                </span>
              )}
            </div>
          </div>
          {PRICING_ENABLED && !user?.has_paid && (
            <Link to="/checkout" data-testid="profile-upgrade">
              <Button className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm font-semibold">
                <Sparkles className="h-4 w-4 mr-1.5" /> Unlock Full Course
              </Button>
            </Link>
          )}
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {stats.map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }} className="p-5 rounded-sm border border-border/70 bg-card" data-testid={`profile-stat-${i}`}>
              <s.icon className="h-5 w-5 text-primary mb-4" />
              <div className="font-mono text-2xl font-bold">{s.value}</div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{s.label}</div>
            </motion.div>
          ))}
        </div>

        <h2 className="font-display text-2xl font-bold mb-5 flex items-center gap-2"><Medal className="h-6 w-6 text-primary" /> Badges</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-10" data-testid="profile-badges">
          {badges.map((b) => {
            const Icon = getIcon(b.icon);
            const has = earned.has(b.id);
            return (
              <div key={b.id} className={`p-4 rounded-sm border text-center transition-colors ${
                has ? "border-primary/40 bg-primary/5" : "border-border/60 bg-card opacity-50"}`} data-testid={`badge-${b.id}`}>
                <Icon className={`h-7 w-7 mx-auto mb-2 ${has ? "text-primary" : "text-muted-foreground"}`} />
                <div className="text-xs font-medium leading-tight">{b.name}</div>
                <div className="text-[10px] text-muted-foreground mt-1 leading-tight">{b.desc}</div>
                {has && awards[b.id] && (
                  <div className="text-[9px] font-mono uppercase tracking-widest text-primary/80 mt-2" data-testid={`badge-${b.id}-date`}>
                    Earned {new Date(awards[b.id]).toLocaleDateString()}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <h2 className="font-display text-2xl font-bold mb-5 flex items-center gap-2"><TrendingUp className="h-6 w-6 text-primary" /> Academy Progress</h2>
        <div className="space-y-3 mb-10" data-testid="profile-academies">
          {academies.map((a) => {
            const Icon = getIcon(a.icon);
            const ap = academyProgress(a);
            return (
              <div key={a.slug} className="flex items-center gap-4 p-4 rounded-sm border border-border/70 bg-card">
                <Icon className="h-5 w-5 text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm truncate">{a.emoji} {a.title}</span>
                    <span className="font-mono text-xs text-primary shrink-0">{ap.active ? `${ap.pct}%` : "Soon"}</span>
                  </div>
                  <Progress value={ap.pct} className="h-1.5 bg-secondary mt-2" />
                </div>
              </div>
            );
          })}
        </div>

        <h2 className="font-display text-2xl font-bold mb-5 flex items-center gap-2"><ScrollText className="h-6 w-6 text-primary" /> Certificates</h2>
        <div className="p-6 rounded-sm border border-dashed border-border bg-card text-center" data-testid="profile-certificates">
          <Lock className="h-7 w-7 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-slate-300 font-medium">No certificates yet</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
            Complete an Academy's capstone to earn a verified Certificate of Completion with your name, the academy, and a unique verification ID.
          </p>
        </div>

        <h2 className="font-display text-2xl font-bold mb-5 mt-10 flex items-center gap-2"><KeyRound className="h-6 w-6 text-primary" /> Account Security</h2>
        <ChangePasswordCard />
      </main>
    </div>
  );
}
